import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Form, Button, Card, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { FaKey, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import { motion } from 'framer-motion';

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [userId, setUserId] = useState(null);

  const mainColor = '#006186';

  useEffect(() => {
    // Получаем токен из URL
    const params = new URLSearchParams(location.search);
    const tokenParam = params.get('token');
    
    if (!tokenParam) {
      setError('Токен восстановления не найден');
      setVerifying(false);
      return;
    }
    
    setToken(tokenParam);
    verifyToken(tokenParam);
  }, [location]);

  const verifyToken = async (tokenParam) => {
    try {
      const response = await fetch('/api/auth/verify-reset-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: tokenParam })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setTokenValid(true);
        setUserId(data.userId);
      } else {
        setError(data.error || 'Недействительная ссылка восстановления');
        setTokenValid(false);
      }
    } catch (err) {
      setError('Ошибка проверки ссылки восстановления');
      setTokenValid(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('Пароль должен содержать не менее 6 символов');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          newPassword: newPassword
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        setError(data.error || 'Ошибка сброса пароля');
      }
    } catch (err) {
      setError('Ошибка соединения с сервером');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="bg-light min-vh-100 d-flex align-items-center">
        <Container>
          <Row className="justify-content-center">
            <Col lg={6} md={8}>
              <Card className="border-0 shadow-sm rounded-4 text-center p-5">
                <Spinner animation="border" variant="primary" className="mx-auto mb-3" />
                <p className="text-muted">Проверка ссылки восстановления...</p>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="bg-light min-vh-100 d-flex align-items-center">
        <Container>
          <Row className="justify-content-center">
            <Col lg={6} md={8}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                  <div style={{ height: '6px', backgroundColor: '#dc3545' }}></div>
                  <Card.Body className="p-5 text-center">
                    <FaExclamationCircle size={60} className="text-danger mb-3" />
                    <h3 className="fw-bold mb-3">Ошибка</h3>
                    <Alert variant="danger" className="border-0">
                      {error || 'Ссылка восстановления недействительна или истекла'}
                    </Alert>
                    <Button 
                      onClick={() => navigate('/')}
                      className="rounded-pill px-4 py-2"
                      style={{ backgroundColor: mainColor }}
                    >
                      Вернуться на главную
                    </Button>
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (success) {
    return (
      <div className="bg-light min-vh-100 d-flex align-items-center">
        <Container>
          <Row className="justify-content-center">
            <Col lg={6} md={8}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                  <div style={{ height: '6px', backgroundColor: '#28a745' }}></div>
                  <Card.Body className="p-5 text-center">
                    <FaCheckCircle size={60} className="text-success mb-3" />
                    <h3 className="fw-bold mb-3">Пароль успешно изменен!</h3>
                    <p className="text-muted mb-4">
                      Вы будете перенаправлены на главную страницу через несколько секунд.
                    </p>
                    <Button 
                      onClick={() => navigate('/')}
                      className="rounded-pill px-4 py-2"
                      style={{ backgroundColor: mainColor }}
                    >
                      Перейти на главную
                    </Button>
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div className="bg-light min-vh-100 d-flex align-items-center py-5">
      <Container>
        <Row className="justify-content-center">
          <Col lg={6} md={8}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                <div style={{ height: '6px', backgroundColor: mainColor }}></div>
                <Card.Body className="p-4 p-md-5">
                  <div className="text-center mb-4">
                    <div className="bg-primary bg-opacity-10 d-inline-block p-4 rounded-circle mb-3" style={{ color: mainColor }}>
                      <FaKey size={40} />
                    </div>
                    <h2 className="fw-bold">Создание нового пароля</h2>
                    <p className="text-muted small">
                      Введите новый пароль для вашей учетной записи
                    </p>
                  </div>

                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label className="small fw-bold text-muted text-uppercase">
                        Новый пароль
                      </Form.Label>
                      <Form.Control
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Минимум 6 символов"
                        className="bg-light border-0 py-2 px-3 rounded-3"
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <Form.Label className="small fw-bold text-muted text-uppercase">
                        Подтверждение пароля
                      </Form.Label>
                      <Form.Control
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Введите пароль еще раз"
                        className="bg-light border-0 py-2 px-3 rounded-3"
                        required
                      />
                    </Form.Group>

                    {error && (
                      <Alert variant="danger" className="border-0 py-2 px-3 mb-3">
                        {error}
                      </Alert>
                    )}

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-100 rounded-pill py-3 fw-bold shadow-sm border-0"
                      style={{ backgroundColor: mainColor }}
                    >
                      {loading ? 'Сохранение...' : 'Сохранить новый пароль'}
                    </Button>
                  </Form>

                  <div className="text-center mt-4">
                    <Button
                      variant="link"
                      onClick={() => navigate('/')}
                      className="text-decoration-none"
                      style={{ color: mainColor }}
                    >
                      Вернуться на главную
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </motion.div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ResetPassword;